import { useCallback, useEffect, useMemo, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { ArrowLeft, CalendarBlank, Envelope, Phone, Buildings, TrendUp,
         ClipboardText, FilePdf, ChartLine, Users, ShieldCheck, IdentificationBadge,
         Briefcase, ArrowsClockwise, UsersThree, Clock } from "@phosphor-icons/react";
import { Card, CardContent } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Skeleton } from "../components/ui/skeleton";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";
import {
  LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
} from "recharts";
import api from "../lib/api";
import { useAuth } from "../lib/auth";
import { toast } from "sonner";
import { EmployeeUserAccess } from "../components/EmployeeUserAccess";
import { formatDate, scoreBandClass, scoreBandLabel } from "../lib/utils-extra";

const STATUS_COLOR = {
  draft: "bg-yellow-50 text-[#D97706] border-yellow-200",
  submitted: "bg-blue-50 text-[#0284C7] border-blue-200",
  finalized: "bg-green-50 text-[#16A34A] border-green-200",
};

export default function EmployeeHistory() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);

  const canViewAudit = ["owner", "hr"].includes(user?.role);

  useEffect(() => {
    let active = true;
    api.get(`/employees/${id}/history`)
      .then(({ data }) => { if (active) { setData(data); setError(null); } })
      .catch((e) => { if (active) setError(e?.response?.status === 403 ? "forbidden" : "error"); });
    return () => { active = false; };
  }, [id]);

  if (error) {
    return (
      <div className="space-y-6" data-testid="employee-history-error">
        <button onClick={() => navigate("/employees")} className="text-sm text-[#475569] flex items-center gap-1" data-testid="employee-back-button">
          <ArrowLeft size={14} /> Back to employees
        </button>
        <Card className="surface-card border-[#E2E8F0]">
          <CardContent className="p-10 text-center">
            <ShieldCheck size={36} weight="duotone" color="#94A3B8" className="mx-auto mb-3" />
            <div className="font-display text-lg font-semibold">
              {error === "forbidden" ? "You don't have access to this profile" : "Unable to load profile"}
            </div>
            <p className="text-sm text-[#475569] mt-1">
              {error === "forbidden"
                ? "You can only view employees within your reporting scope."
                : "Something went wrong while loading this employee. Please try again."}
            </p>
            <Button variant="outline" className="mt-5" onClick={() => navigate("/employees")} data-testid="employee-error-back">
              Back to employees
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (!data) {
    return (
      <div className="space-y-4">
        <Skeleton className="h-32 rounded-2xl" />
        <Skeleton className="h-72 rounded-2xl" />
      </div>
    );
  }

  const { employee, position, supervisor, direct_reports, evaluations, stats, trend } = data;
  const latestScore = stats.latest_score ?? 0;

  const downloadPdf = async (eid) => {
    try {
      const res = await api.get(`/evaluations/${eid}/pdf`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url; a.download = `evaluation_${eid}.pdf`; a.click();
      URL.revokeObjectURL(url);
    } catch {
      toast.error("Unable to download the evaluation PDF.");
    }
  };

  return (
    <div className="space-y-6" data-testid="employee-history-page">
      <button onClick={() => navigate("/employees")} className="text-sm text-[#475569] flex items-center gap-1" data-testid="employee-back-button">
        <ArrowLeft size={14} /> Back to employees
      </button>

      {/* Shared identity header */}
      <div className="flex items-start gap-4 flex-wrap">
        <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-[#0EA5E9] to-[#0284C7] flex items-center justify-center text-white font-display font-semibold text-2xl shrink-0">
          {employee.full_name?.split(" ").map(n => n[0]).slice(0, 2).join("")}
        </div>
        <div className="flex-1 min-w-0">
          <div className="small-label">Employee profile</div>
          <h1 className="font-display text-3xl font-semibold mt-1" data-testid="employee-name">{employee.full_name}</h1>
          <div className="flex flex-wrap gap-3 mt-2 text-sm text-[#475569]">
            <span className="flex items-center gap-1"><Buildings size={14} /> {position?.name || "—"} · {employee.department || "—"}</span>
            {employee.email && <span className="flex items-center gap-1"><Envelope size={14} /> {employee.email}</span>}
            {employee.phone && <span className="flex items-center gap-1"><Phone size={14} /> {employee.phone}</span>}
          </div>
        </div>
        <Badge className={employee.active ? "bg-[#16A34A]/10 text-[#16A34A] border-0" : "bg-[#F1F5F9] text-[#475569] border-0"} data-testid="employee-status-badge">
          {employee.active ? employee.employment_status : "Inactive"}
        </Badge>
      </div>

      <Tabs defaultValue="overview" className="w-full">
        <TabsList className="bg-[#F1F5F9] flex-wrap h-auto" data-testid="employee-profile-tabs">
          <TabsTrigger value="overview" data-testid="employee-tab-overview">Overview</TabsTrigger>
          {canViewAudit && (
            <TabsTrigger value="user-access" data-testid="employee-tab-user-access">User Access</TabsTrigger>
          )}
          <TabsTrigger value="kpi-history" data-testid="employee-tab-kpi-history">KPI History</TabsTrigger>
          {canViewAudit && (
            <TabsTrigger value="audit" data-testid="employee-tab-audit">Audit Trail</TabsTrigger>
          )}
        </TabsList>

        {/* ---------------- OVERVIEW ---------------- */}
        <TabsContent value="overview" className="space-y-6 mt-6" data-testid="employee-overview-tab">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <StatCard label="Evaluations" value={stats.total_evaluations} icon={ClipboardText} />
            <StatCard label="Finalized" value={stats.finalized_count} icon={TrendUp} />
            <StatCard label="Average score" value={stats.average_score != null ? `${stats.average_score.toFixed(1)}%` : "—"} icon={ChartLine} />
            <StatCard label="Next review" value={employee.next_evaluation_date ? formatDate(employee.next_evaluation_date) : "—"} icon={CalendarBlank} />
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            <Card className={`surface-card border-0 ${scoreBandClass(latestScore)}`}>
              <CardContent className="p-6 text-white">
                <div className="small-label text-white/80">Latest score</div>
                {stats.latest_date ? (
                  <>
                    <div className="font-display text-5xl font-semibold mt-2">
                      {latestScore.toFixed(1)}<span className="text-2xl opacity-80">%</span>
                    </div>
                    <div className="text-sm opacity-90 mt-1">
                      {stats.latest_interpretation || scoreBandLabel(latestScore)}
                    </div>
                    <div className="text-xs opacity-80 mt-3">as of {formatDate(stats.latest_date)}</div>
                  </>
                ) : (
                  <div className="text-sm opacity-90 mt-4">No finalized evaluations yet.</div>
                )}
              </CardContent>
            </Card>

            {/* Employment summary (read-only, existing fields only) */}
            <Card className="surface-card border-[#E2E8F0] lg:col-span-2" data-testid="employment-summary-card">
              <CardContent className="p-6">
                <div className="font-display text-lg font-semibold flex items-center gap-2 mb-4">
                  <Briefcase size={18} weight="duotone" color="#0EA5E9" /> Employment summary
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-4">
                  <SummaryItem icon={IdentificationBadge} label="Position" value={position?.name} />
                  <SummaryItem icon={Buildings} label="Department" value={employee.department} />
                  <SummaryItem icon={Buildings} label="Branch" value={employee.branch_name} />
                  <SummaryItem icon={UsersThree} label="Team" value={employee.team_name} />
                  <SummaryItem icon={Clock} label="Work schedule" value={employee.work_schedule_name} />
                  <SummaryItem icon={ShieldCheck} label="Employment status" value={employee.employment_status} />
                  <SummaryItem icon={CalendarBlank} label="Date hired" value={employee.date_hired ? formatDate(employee.date_hired) : null} />
                  <SummaryItem icon={ArrowsClockwise} label="Evaluation frequency" value={employee.evaluation_frequency} capitalize />
                  <SummaryItem icon={CalendarBlank} label="Next review" value={employee.next_evaluation_date ? formatDate(employee.next_evaluation_date) : null} />
                  <div>
                    <div className="small-label flex items-center gap-1.5"><Users size={13} /> Reports to</div>
                    <div className="text-sm font-medium mt-1">
                      {supervisor ? (
                        <Link to={`/employees/${supervisor.id}`} className="text-[#0EA5E9] hover:underline" data-testid="supervisor-link">
                          {supervisor.full_name}
                        </Link>
                      ) : <span className="text-[#475569]">—</span>}
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          {direct_reports?.length > 0 && (
            <Card className="surface-card border-[#E2E8F0]">
              <CardContent className="p-5">
                <div className="font-display text-base font-semibold flex items-center gap-2 mb-3">
                  <Users size={18} weight="duotone" color="#0EA5E9" /> Direct reports ({direct_reports.length})
                </div>
                <div className="flex flex-wrap gap-2">
                  {direct_reports.map((dr) => (
                    <Link key={dr.id} to={`/employees/${dr.id}`}
                          className="pill hover:bg-[#0EA5E9]/10 hover:text-[#0EA5E9]" data-testid={`direct-report-${dr.id}`}>
                      {dr.full_name}
                    </Link>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}
        </TabsContent>

        {/* ---------------- KPI HISTORY ---------------- */}
        <TabsContent value="kpi-history" className="space-y-6 mt-6" data-testid="employee-kpi-history-tab">
          <Card className="surface-card border-[#E2E8F0]">
            <CardContent className="p-6">
              <div className="small-label">Score trend</div>
              <div className="font-display text-lg font-semibold mt-1 mb-3">Over time</div>
              <div className="h-56">
                {trend.length === 0 ? (
                  <div className="h-full flex items-center justify-center text-sm text-[#475569]">
                    Trend will appear once at least one evaluation is finalized.
                  </div>
                ) : (
                  <ResponsiveContainer width="100%" height="100%" minHeight={200}>
                    <LineChart data={trend} margin={{ top: 10, right: 10, bottom: 5, left: -20 }}>
                      <CartesianGrid stroke="#E2E8F0" strokeDasharray="3 3" />
                      <XAxis dataKey="date" tick={{ fontSize: 10, fill: "#475569" }} />
                      <YAxis domain={[0, 100]} tick={{ fontSize: 11, fill: "#475569" }} />
                      <Tooltip contentStyle={{ borderRadius: 12, border: "1px solid #E2E8F0", background: "#fff" }} />
                      <Line type="monotone" dataKey="score" stroke="#0EA5E9" strokeWidth={2.5}
                            dot={{ r: 4, fill: "#0EA5E9" }} activeDot={{ r: 6 }} />
                    </LineChart>
                  </ResponsiveContainer>
                )}
              </div>
            </CardContent>
          </Card>

          <Card className="surface-card border-[#E2E8F0]">
            <CardContent className="p-5">
              <div className="font-display text-lg font-semibold mb-4">Evaluation timeline</div>
              {evaluations.length === 0 ? (
                <div className="py-8 text-center text-sm text-[#475569]">
                  No evaluations yet. <Link to="/evaluations/new" className="text-[#0EA5E9] hover:underline">Start one now →</Link>
                </div>
              ) : (
                <div className="space-y-3">
                  {evaluations.map((e) => (
                    <div key={e.id} className="flex items-start gap-4 p-3 rounded-xl border border-[#E2E8F0] hover:bg-[#F8FAFC]"
                         data-testid={`history-eval-${e.id}`}>
                      <div className="w-2.5 h-2.5 rounded-full bg-[#0EA5E9] mt-2 shrink-0" />
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <div className="font-medium">{formatDate(e.evaluation_date)}</div>
                          <Badge className={`${STATUS_COLOR[e.status]} capitalize text-[10px]`}>{e.status}</Badge>
                          {e.final_score != null && (
                            <Badge className={`${scoreBandClass(e.final_score)} text-white border-0 text-[10px]`}>
                              {e.final_score.toFixed(1)}% · {e.interpretation || scoreBandLabel(e.final_score)}
                            </Badge>
                          )}
                        </div>
                        <div className="text-xs text-[#475569] mt-1">
                          {formatDate(e.period_start)} → {formatDate(e.period_end)} · Evaluated by {e.evaluator_name || "—"}
                        </div>
                      </div>
                      <div className="flex gap-2 shrink-0">
                        <Link to={`/evaluations/${e.id}`}>
                          <Button variant="outline" size="sm" data-testid={`history-open-${e.id}`}>Open</Button>
                        </Link>
                        {e.status === "finalized" && (
                          <Button variant="ghost" size="sm" onClick={() => downloadPdf(e.id)} data-testid={`history-pdf-${e.id}`}>
                            <FilePdf size={14} className="mr-1" /> PDF
                          </Button>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* ---------------- USER ACCESS ---------------- */}
        {canViewAudit && (
          <TabsContent value="user-access" className="mt-6" data-testid="employee-user-access-tab">
            <EmployeeUserAccess employeeId={id} employeeName={employee.full_name} employeeEmail={employee.email} />
          </TabsContent>
        )}

        {/* ---------------- AUDIT TRAIL ---------------- */}
        {canViewAudit && (
          <TabsContent value="audit" className="mt-6" data-testid="employee-audit-tab">
            <EmployeeAuditTrail employeeId={id} evaluations={evaluations} />
          </TabsContent>
        )}
      </Tabs>
    </div>
  );
}

function SummaryItem({ icon: Icon, label, value, capitalize }) {
  return (
    <div>
      <div className="small-label flex items-center gap-1.5"><Icon size={13} /> {label}</div>
      <div className={`text-sm font-medium mt-1 ${value ? "" : "text-[#475569]"} ${capitalize ? "capitalize" : ""}`}>
        {value || "—"}
      </div>
    </div>
  );
}

function StatCard({ label, value, icon: Icon }) {
  return (
    <Card className="surface-card border-[#E2E8F0]">
      <CardContent className="p-4">
        <div className="flex items-center justify-between mb-2">
          <div className="small-label">{label}</div>
          <Icon size={16} weight="duotone" color="#0EA5E9" />
        </div>
        <div className="font-display text-2xl font-semibold">{value}</div>
      </CardContent>
    </Card>
  );
}

/**
 * Employee-scoped audit trail. Reuses the existing /audit-logs endpoint (owner/hr only)
 * and filters client-side to entries about this employee record or their evaluations.
 * No backend or DB changes (Phase 1).
 */
function EmployeeAuditTrail({ employeeId, evaluations }) {
  const [logs, setLogs] = useState(null);
  const evalIds = useMemo(() => new Set((evaluations || []).map((e) => e.id)), [evaluations]);

  const fetchLogs = useCallback(() => {
    api.get("/audit-logs", { params: { limit: 500 } })
      .then(({ data }) => setLogs(data.filter((l) => l.record_id === employeeId || evalIds.has(l.record_id))))
      .catch(() => setLogs([]));
  }, [employeeId, evalIds]);

  useEffect(() => { fetchLogs(); }, [fetchLogs]);

  const refresh = () => { setLogs(null); fetchLogs(); };

  const formatTs = (iso) => {
    if (!iso) return "—";
    return new Date(iso).toLocaleString("en-US", {
      month: "short", day: "numeric", year: "numeric", hour: "2-digit", minute: "2-digit",
    });
  };
  const humanize = (a) => (a || "").replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());

  return (
    <Card className="surface-card border-[#E2E8F0]">
      <CardContent className="p-5">
        <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
          <div className="font-display text-lg font-semibold flex items-center gap-2">
            <ShieldCheck size={18} weight="duotone" color="#0EA5E9" /> Audit Trail
          </div>
          <Button variant="outline" size="sm" onClick={refresh} data-testid="employee-audit-refresh">
            <ArrowsClockwise size={14} className="mr-1" /> Refresh
          </Button>
        </div>
        <p className="text-xs text-[#475569] -mt-2 mb-4">Sensitive actions on this employee record and their evaluations.</p>

        {logs === null ? (
          <div className="py-8 text-center text-sm text-[#475569]">Loading…</div>
        ) : logs.length === 0 ? (
          <div className="py-8 text-center text-sm text-[#475569]" data-testid="employee-audit-empty">
            No audit entries for this employee yet.
          </div>
        ) : (
          <div className="space-y-2" data-testid="employee-audit-list">
            {logs.map((l) => (
              <div key={l.id} className="flex items-start gap-3 p-3 rounded-xl border border-[#E2E8F0]"
                   data-testid={`employee-audit-row-${l.id}`}>
                <div className="w-2 h-2 rounded-full bg-[#0EA5E9] mt-2 shrink-0" />
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-medium text-sm">{humanize(l.action)}</span>
                    {l.record_label && <Badge className="bg-[#F1F5F9] text-[#475569] border-0 text-[10px]">{l.record_label}</Badge>}
                  </div>
                  <div className="text-xs text-[#475569] mt-1">
                    {l.user_name || "—"} ({l.role || "—"}) · {formatTs(l.timestamp)}{l.ip_address ? ` · ${l.ip_address}` : ""}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
